import React, { useMemo } from 'react';
import { batch } from 'react-redux';
import {
  removeErrorAction,
  setCohortRowTimeWindow,
  setCohortRowTimeWindowValues,
} from '../../../../redux/slices/oldBuilder';
import styled from 'styled-components';
import {
  cohortConfigTypeSelector,
  eventTimeWindowSelector,
} from '../../../../redux/selectors/builder';
import BuilderTimeBracketInput from './BuilderTimeBracketInput';
import { SingleSelectMenu } from '../../../Dropdown';
import {
  FunnelModeKey,
  TimeWindowKey,
} from '../../../../utils/apollo/graphqlSchema';
import BuilderNumberInputField from '../../BuilderFields/BuilderNumberInputField';
import { MAX_TIME_WINDOW } from '../../../../config/constants';
import { CohortRowRelatedInfo } from './CohortRowUI';
import { cohortTimeWindowTooltips } from '../../../KubitTooltip/globalTooltips';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import BuilderNumericalMenu from '../../BuilderFields/BuilderNumericalMenu';
import { optionsForDays } from '../../BuilderFields/BuilderNumericalMenu/utils';
import { parseRetentionUnit } from './utils';
import useAnalysisBuilderInfo from '../../../../redux/selectorHooks/useAnalysisBuilderInfo';
import { useCohortRowContext } from './CohortRowContext';
import * as BuilderUI from '../../BuilderUI';
import CohortInEachTimeWindowValue from './CohortInEachTimeWindowValue';

const TIME_WINDOW_VALUES_ERROR_KEY = 'timeWindowValues';

const TextWrapper = styled(BuilderUI.BuilderFieldLabel)`
  align-self: center;
  margin-right: 0;
`;

export default () => {
  const dispatch = useAppDispatch();
  const { rowMeta, cohortRow } = useCohortRowContext();
  const {
    analysisId,
    simpleCohortTypeKey,
    timeWindow: cohortTimeWindow,
  } = cohortRow;

  const memoEventTimeWindowSelector = useMemo(eventTimeWindowSelector, []);

  const lowerEventTimeWindowValue = useAppSelector((state) =>
    memoEventTimeWindowSelector(state, { ...rowMeta, numberIndex: 0 }),
  );
  const upperEventTimeWindowValue = useAppSelector((state) =>
    memoEventTimeWindowSelector(state, { ...rowMeta, numberIndex: 1 }),
  );
  const cohortConfigSelector = useAppSelector((state) =>
    cohortConfigTypeSelector(state, simpleCohortTypeKey),
  );

  const cohortTimeWindowOptions = cohortConfigSelector?.timeWindows;

  const analysisBuilderInfo = useAnalysisBuilderInfo(analysisId);
  const builderInfo = analysisBuilderInfo?.query;

  const isFunnelTimeToConvertMode =
    builderInfo?.__typename === 'FunnelQuery' &&
    builderInfo?.mode?.key === FunnelModeKey.TimeToConvert;

  const handleTimeWindowChange = (newValue: string) => {
    dispatch(
      setCohortRowTimeWindow({ rowMeta, newValue: newValue as TimeWindowKey }),
    );
  };

  const handleTimeWindowValueChange = (
    newValue: number | null,
    numberIndex: number,
  ) => {
    const updatedRowMeta = { ...rowMeta, numberIndex };
    batch(() => {
      dispatch(
        setCohortRowTimeWindowValues({
          newValue,
          rowMeta: updatedRowMeta,
        }),
      );
      dispatch(
        removeErrorAction({
          rowMeta: updatedRowMeta,
          errorKey: TIME_WINDOW_VALUES_ERROR_KEY,
        }),
      );
    });
  };

  return (
    <CohortRowRelatedInfo>
      {!isFunnelTimeToConvertMode && (
        <SingleSelectMenu
          options={
            cohortTimeWindowOptions?.map((option) => ({
              text: option.name,
              value: option.key,
              tooltip: cohortTimeWindowTooltips[option.key],
            })) ?? []
          }
          value={cohortTimeWindow}
          onChange={handleTimeWindowChange}
          menuMaxHeight="auto"
        />
      )}
      {(cohortTimeWindow === TimeWindowKey.RollingBack ||
        cohortTimeWindow === TimeWindowKey.RollingForward ||
        cohortTimeWindow === TimeWindowKey.Within) && (
        <>
          {cohortTimeWindow === TimeWindowKey.Within &&
          builderInfo?.__typename === 'RetentionQuery' &&
          builderInfo.customTimeBrackets.length > 0 ? (
            <BuilderTimeBracketInput
              customTimeBrackets={builderInfo.customTimeBrackets}
              retentionUnit={builderInfo.retentionUnit}
              rowMeta={{ ...rowMeta, numberIndex: 0 }}
              type={TIME_WINDOW_VALUES_ERROR_KEY}
              value={lowerEventTimeWindowValue}
            />
          ) : (
            <BuilderNumericalMenu
              trigger={
                <BuilderNumberInputField
                  placeholder="eg. 5"
                  value={lowerEventTimeWindowValue}
                  max={MAX_TIME_WINDOW}
                  rowMeta={{ ...rowMeta, numberIndex: 0 }}
                  errorKey={TIME_WINDOW_VALUES_ERROR_KEY}
                  readOnly
                />
              }
              options={optionsForDays}
              value={lowerEventTimeWindowValue}
              onChange={(newValue: number) =>
                handleTimeWindowValueChange(newValue, 0)
              }
              max={MAX_TIME_WINDOW}
              placeholder="Enter a value"
            />
          )}
          {builderInfo?.__typename === 'RetentionQuery' &&
            builderInfo.customTimeBrackets.length === 0 && (
              <TextWrapper>
                {parseRetentionUnit(builderInfo.retentionUnit)}
              </TextWrapper>
            )}
        </>
      )}
      {(cohortTimeWindow === TimeWindowKey.RollingBackBetween ||
        cohortTimeWindow === TimeWindowKey.RollingForwardBetween ||
        cohortTimeWindow === TimeWindowKey.BetweenSinceStart) && (
        <>
          {builderInfo?.__typename === 'RetentionQuery' &&
          builderInfo.customTimeBrackets.length > 0 ? (
            <BuilderTimeBracketInput
              customTimeBrackets={builderInfo.customTimeBrackets}
              retentionUnit={builderInfo.retentionUnit}
              rowMeta={{ ...rowMeta, numberIndex: 0 }}
              type={TIME_WINDOW_VALUES_ERROR_KEY}
              value={lowerEventTimeWindowValue}
            />
          ) : (
            <BuilderNumericalMenu
              trigger={
                <BuilderNumberInputField
                  placeholder="eg. 5"
                  value={lowerEventTimeWindowValue}
                  max={MAX_TIME_WINDOW}
                  rowMeta={{ ...rowMeta, numberIndex: 0 }}
                  errorKey={TIME_WINDOW_VALUES_ERROR_KEY}
                  readOnly
                />
              }
              options={optionsForDays}
              value={lowerEventTimeWindowValue}
              onChange={(newValue: number) =>
                handleTimeWindowValueChange(newValue, 0)
              }
              max={MAX_TIME_WINDOW}
              placeholder="Enter a value"
            />
          )}

          <TextWrapper>and</TextWrapper>
          {builderInfo?.__typename === 'RetentionQuery' &&
          builderInfo.customTimeBrackets.length > 0 ? (
            <BuilderTimeBracketInput
              customTimeBrackets={builderInfo.customTimeBrackets}
              retentionUnit={builderInfo.retentionUnit}
              rowMeta={{ ...rowMeta, numberIndex: 1 }}
              type={TIME_WINDOW_VALUES_ERROR_KEY}
              value={upperEventTimeWindowValue}
            />
          ) : (
            <BuilderNumericalMenu
              trigger={
                <BuilderNumberInputField
                  placeholder="eg. 5"
                  value={upperEventTimeWindowValue}
                  max={MAX_TIME_WINDOW}
                  rowMeta={{ ...rowMeta, numberIndex: 1 }}
                  errorKey={TIME_WINDOW_VALUES_ERROR_KEY}
                  readOnly
                />
              }
              options={optionsForDays}
              value={upperEventTimeWindowValue}
              onChange={(newValue: number) =>
                handleTimeWindowValueChange(newValue, 1)
              }
              max={MAX_TIME_WINDOW}
              placeholder="Enter a value"
            />
          )}
          {builderInfo?.__typename === 'RetentionQuery' &&
            builderInfo.customTimeBrackets.length === 0 && (
              <TextWrapper>
                {parseRetentionUnit(builderInfo.retentionUnit)}
              </TextWrapper>
            )}
        </>
      )}
      {cohortTimeWindow === TimeWindowKey.Current && (
        <TextWrapper>Day(s)</TextWrapper>
      )}
      {cohortTimeWindow === TimeWindowKey.InEach && (
        <CohortInEachTimeWindowValue />
      )}
    </CohortRowRelatedInfo>
  );
};
